import React, {
  useState,
  useMemo,
  memo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
} from "@tanstack/react-table";
import { SearchInput, Loading, Button } from "../";
import { useCtrlF, useEscapeKey } from "../../Hooks";
import Icon from "../../assets/icons/Icon";

const DataTable = ({
  columns,
  fetchData,
  currentPage = 1,
  onPageChange,
  onRecordsPerPageChange,
  recordsPerPage = 200,
  searchTerm = "",
  onSearchChange,
  isSearchable = true,
  isPagination = true,
  isPageSize = true,
  isLoading = false,
  handleSearchKey,
  isCheckBox = false,
  isIndexing = true,
  onSelectionChange,
  data,
  pages,
  isExportPdf = false,
  isExportCsv = false,
  exportToPDF,
  exportToCSV,
  pdfLoading,
  isChangeRowColor = false,
  handleChangeRowColor,
  handleRemoveRowColor,
  isExportPng,
  exportToPNG,
}) => {
  const tableRef = useRef();
  const [sorting, setSorting] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const handleFullScreenTable = () => {
    const elem = tableRef.current;
    if (!elem) return;

    try {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(); // Safari
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen(); // IE11
      }
    } catch (error) {
      console.log("Fullscreen error:", error);
    }
  };

  useCtrlF(() => {
    handleFullScreenTable();
  });

  useEscapeKey(() => {
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (document.webkitFullscreenElement) {
        document.webkitExitFullscreen();
      } else if (document.msFullscreenElement) {
        document.msExitFullscreen();
      }
    } catch (error) {
      console.log("Escape fullscreen error:", error);
    }
  });

  // Persist selected rows only if they exist in the new data
  useEffect(() => {
    setSelectedRows((prevSelected) => {
      const newIds = new Set(data?.map((row) => row._id));
      const updatedSelected = new Set(
        [...prevSelected].filter((id) => newIds.has(id))
      );
      return updatedSelected;
    });
  }, [data]);

  // Notify parent of selected rows changes
  useEffect(() => {
    if (onSelectionChange) {
      const selectedIds = Array.from(selectedRows);
      onSelectionChange(selectedIds);
    }
  }, [selectedRows, onSelectionChange]);

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      const allRowIds = data.map((row) => row._id);
      setSelectedRows(new Set(allRowIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleRowSelect = (rowId) => {
    setSelectedRows((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(rowId)) {
        newSelected.delete(rowId);
      } else {
        newSelected.add(rowId);
      }
      return newSelected;
    });
  };

  const isHeaderChecked = selectedRows.size === data?.length && data.length > 0;

  const indexedColumns = useMemo(() => {
    const columnsWithIndex = isIndexing
      ? [
          {
            id: "index",
            header: "#",
            cell: ({ row }) =>
              row.index + 1 + (currentPage - 1) * recordsPerPage,
          },
        ]
      : [];

    const columnsWithCheckbox = isCheckBox
      ? [
          {
            id: "checkbox",
            header: (
              <input
                type="checkbox"
                checked={isHeaderChecked}
                onChange={handleSelectAllChange}
              />
            ),
            cell: ({ row }) => (
              <input
                type="checkbox"
                checked={selectedRows.has(row.original._id)}
                onChange={() => handleRowSelect(row.original._id)}
              />
            ),
          },
        ]
      : [];

    return [...columnsWithIndex, ...columnsWithCheckbox, ...columns];
  }, [
    columns,
    currentPage,
    recordsPerPage,
    isIndexing,
    isCheckBox,
    data,
    selectedRows,
    isHeaderChecked,
  ]);

  const table = useReactTable({
    columns: indexedColumns,
    data: data || [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const handleRecordsPerPageChange = (event) => {
    onRecordsPerPageChange(Number(event.target.value));
    onPageChange(1);
  };

  const handleSearch = (val) => {
    onSearchChange(val);
    onPageChange(1);
  };

  const renderPageNumbers = () => {
    const visiblePages = 5;
    const pagesToShow = [];

    let startPage = Math.max(currentPage - Math.floor(visiblePages / 2), 1);
    let endPage = Math.min(startPage + visiblePages - 1, pages);

    if (endPage - startPage + 1 < visiblePages) {
      startPage = Math.max(endPage - visiblePages + 1, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pagesToShow.push(
        <button
          key={i}
          className={`btn ${
            i === currentPage ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    return pagesToShow;
  };

  const handleHeaderClick = useCallback((column) => {
    column.toggleSorting(column.getIsSorted() === "asc");
  }, []);

  return (
    <>
      {(isPageSize || isPagination) && (
        <div className="sleet-bobs ">
          {isPageSize && (
            <div className="select-after">
              <select
                className="form-select table_curt_select"
                value={recordsPerPage}
                onChange={handleRecordsPerPageChange}
              >
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center gap-2">
            {isChangeRowColor && selectedRows.size > 0 && (
              <>
                <Button
                  className="btn btn-sm btn-primary"
                  onClick={handleChangeRowColor}
                >
                  Change color
                </Button>
              </>
            )}

            {isSearchable && (
              <div className="search-bar-input-table">
                <SearchInput
                  onSearchChange={handleSearch}
                  handleSearchKey={handleSearchKey}
                />
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center gap-1 ">
              {isExportPdf && (
                <>
                  {pdfLoading ? (
                    <Loading />
                  ) : (
                    <Button
                      type="button"
                      className="btn p-0 m-0"
                      onClick={exportToPDF}
                    >
                      <img src="/assets/images/pdf.png" alt="pdf" width={35} />
                    </Button>
                  )}
                </>
              )}
              {isExportCsv && (
                <Button
                  type="button"
                  className="btn p-0 m-0"
                  onClick={exportToCSV}
                >
                  <img src="/assets/images/xls.png" alt="xls" width={35} />
                </Button>
              )}
              {isExportPng && selectedRows.size > 0 && (
                <Button
                  type="button"
                  className="btn p-0 m-0"
                  onClick={exportToPNG}
                >
                  <img src="/assets/images/png.png" alt="xls" width={35} />
                </Button>
              )}

              <Button
                className="btn btn-primary"
                onClick={handleFullScreenTable}
                title="Toggle fullscreen (Ctrl + F)"
              >
                <Icon name="MdFullscreen" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {data ? (
        <div className="recentOrders" ref={tableRef}>
          <table className="report-card-box">
            <thead className="table-light">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={() => handleHeaderClick(header.column)}
                      style={{ cursor: "pointer" }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getIsSorted() === "asc" ? " ▲" : ""}
                      {header.column.getIsSorted() === "desc" ? " ▼" : ""}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={indexedColumns?.length} className="text-center">
                    <Loading />
                  </td>
                </tr>
              ) : data && data.length === 0 ? (
                <tr>
                  <td colSpan={indexedColumns?.length} className="text-center">
                    No records found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr key={index}>
                    {row.getVisibleCells().map((cell, index1) => (
                      <td
                        key={index1}
                        style={{
                          backgroundColor: row.original.AG || "",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        "No Record"
      )}

      {isPagination && !isLoading && (
        <div className="pagination page_ageChange_box">
          <button
            className="btn btn-outline-primary"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="btn btn-outline-primary "
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            &lt;
          </button>

          {renderPageNumbers()}

          <button
            className="btn btn-outline-primary "
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === pages}
          >
            &gt;
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => onPageChange(pages)}
            disabled={currentPage === pages}
          >
            Last
          </button>
        </div>
      )}
    </>
  );
};

export default memo(DataTable);
